import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, Variants } from 'framer-motion';
import { Code, Smartphone, TrendingUp, Bot, ChevronDown, Image as ImageIcon } from 'lucide-react';

const Services: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const services = [
    {
      icon: Code,
      title: 'Technical Services',
      description: 'Custom websites and applications built with modern, scalable technologies.',
      dropdown: [
        { label: 'Software Development', href: '#contact' },
        { label: 'Application Development', href: '#contact' },
        { label: 'Web Development', href: '#contact' },
      ],
    },
    {
      icon: Smartphone,
      title: 'Branding & Design',
      description: 'Beautiful, intuitive designs that enhance your brand’s digital presence.',
      dropdown: [
        { label: 'UI/UX Design', href: '#contact' },
        { label: 'Logo Design', href: '#contact' },
        { label: 'Graphic Design', href: '#contact' },
        { label: 'Video Production', href: '#contact' },
      ],
    },
    {
      icon: TrendingUp,
      title: 'Digital Marketing',
      description: 'Data-driven digital marketing and sales strategies to boost your growth.',
      dropdown: [
        { label: 'SEO Optimization', href: '#contact' },
        { label: 'Social Media Marketing', href: '#contact' },
        { label: 'Analytics & Reporting', href: '#contact' },
      ],
    },
    {
      icon: Bot,
      title: 'AI Integration',
      description: 'Cutting-edge AI solutions to automate processes and drive innovation.',
      dropdown: [
        { label: 'Chatbots & Assistants', href: '#contact' },
        { label: 'Machine Learning', href: '#contact' },
        { label: 'Process Automation', href: '#contact' },
        { label: 'AI Strategy', href: '#contact' },
        { label: 'Generate AI Image', href: '#ai-image' },
      ],
    },
  ];

  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 10, scale: 0.98 },
    visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: ['easeOut'] } },
  };

  const ServiceCard: React.FC<{ service: typeof services[0] }> = ({ service }) => {
    const [open, setOpen] = useState(false);
    const [aiImage, setAiImage] = useState<string | null>(null);
    const cardRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const closeOnOutside = (e: MouseEvent) => {
        if (cardRef.current && !cardRef.current.contains(e.target as Node)) setOpen(false);
      };
      const closeOnEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
      document.addEventListener('mousedown', closeOnOutside);
      document.addEventListener('keydown', closeOnEsc);
      return () => {
        document.removeEventListener('mousedown', closeOnOutside);
        document.removeEventListener('keydown', closeOnEsc);
      };
    }, []);

    const handleAIGenerate = async () => {
      setAiImage('loading');
      // Simulated AI image generator
      setTimeout(() => {
        setAiImage(`https://picsum.photos/seed/${Math.floor(Math.random() * 1000)}/500/300`);
      }, 1200);
    };

    const Icon = service.icon as React.ComponentType<any>;

    return (
      <motion.div
        ref={cardRef}
        variants={itemVariants}
        onMouseEnter={() => window.innerWidth > 768 && setOpen(true)}
        onMouseLeave={() => window.innerWidth > 768 && setOpen(false)}
        onClick={() => window.innerWidth <= 768 && setOpen((p) => !p)}
        whileHover={{
          y: -2,
          scale: 1.01,
          boxShadow: '0 6px 20px rgba(255, 0, 64, 0.1)',
        }}
        className="relative bg-github-darker p-6 rounded-xl border border-github-border flex flex-col items-start transition-all duration-150 group cursor-pointer will-change-transform"
        style={{ transform: 'translateZ(0)' }}
      >
        {/* Chevron Icon */}
        <div className="absolute top-4 right-4 z-20">
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.2 }}
            className="p-2 rounded-md bg-github-dark/60 border border-github-border"
          >
            <ChevronDown className="w-4 h-4 text-github-muted" />
          </motion.div>
        </div>

        {/* Service Icon */}
        <motion.div
          className="p-3 bg-github-dark rounded-lg border border-github-border mb-4 group-hover:border-github-accent/40 transition-all"
          whileHover={{ scale: 1.05 }}
        >
          <Icon className="w-7 h-7 text-github-accent" />
        </motion.div>

        <h3 className="text-lg font-semibold text-github-text mb-2 group-hover:text-github-accent transition-colors">
          {service.title}
        </h3>
        <p className="text-github-muted text-sm leading-relaxed group-hover:text-github-text/80 transition-colors">
          {service.description}
        </p>

        {/* Optimized Dropdown */}
        <motion.div
          initial={{ opacity: 0, y: -8, scale: 0.97 }}
          animate={open ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: -8, scale: 0.97 }}
          transition={{ duration: 0.2, ease: ['easeOut'] }}
          className={`absolute top-16 right-4 rounded-xl shadow-lg w-56 overflow-hidden z-50 transform-gpu backdrop-blur-md border transition-all duration-200 ${
            open
              ? 'pointer-events-auto border-github-accent/20 bg-github-dark/80'
              : 'pointer-events-none border-transparent bg-transparent'
          }`}
        >
          {service.dropdown.map((item, i) => (
            <motion.a
              key={i}
              href={item.href}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                if (item.label === 'Generate AI Image') handleAIGenerate();
              }}
              initial={{ opacity: 0 }}
              animate={{ opacity: open ? 1 : 0 }}
              transition={{ delay: i * 0.03 }}
              className="block px-4 py-2.5 text-sm text-github-text hover:bg-github-accent/10 hover:text-github-accent transition-all duration-100"
            >
              {item.label}
            </motion.a>
          ))}
        </motion.div>

        {/* AI Image Display */}
        {aiImage && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute inset-0 bg-github-dark/95 flex flex-col items-center justify-center rounded-xl z-50 p-4"
          >
            {aiImage === 'loading' ? (
              <div className="text-github-muted animate-pulse flex items-center gap-2">
                <ImageIcon className="w-5 h-5 animate-spin" />
                <span>Generating image with AI...</span>
              </div>
            ) : (
              <>
                <img
                  src={aiImage}
                  alt="AI Generated"
                  className="rounded-lg border border-github-border shadow-lg max-w-full max-h-64 object-cover mb-4"
                />
                <button
                  onClick={() => setAiImage(null)}
                  className="px-4 py-2 text-sm bg-github-accent text-white rounded-lg hover:bg-github-accent/80 transition-all"
                >
                  Close Preview
                </button>
              </>
            )}
          </motion.div>
        )}
      </motion.div>
    );
  };

  return (
    <section id="services" ref={ref} className="bg-github-dark section-padding">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.4 }}
        >
          <h2 className="section-title gradient-text">Our Services</h2>
          <p className="section-subtitle mb-16">
            Comprehensive digital solutions designed to elevate your business with the power of modern technology and AI.
          </p>
        </motion.div>

        <motion.div
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service) => (
            <ServiceCard key={service.title} service={service} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
